<session-manager>
    <inactivity-warning-modal ref="inactivityModal"></inactivity-warning-modal>

    <script>
        import inactivityWarningModal from './inactivity-warning-modal.tag'
        import { isPublicRoute } from '../../config/routes.js'
        import { getSessionActivityService, getSessionRenewalService, getDebugSessionService, getAuthService } from '../../services/service-loader';

        export default {
            includeStore: true,

            get components() {

                return { inactivityWarningModal };

            },

            async onMounted() {
                console.log('🔐 Session manager mounted')
                
                // Check if we're on a public page using centralized config
                const hash = window.location.hash || ''
                const isPublicPage = isPublicRoute(hash)
                
                if (isPublicPage) {
                    console.log('🔐 On public page, skipping session initialization')
                    return
                }
                
                // Initialize activity tracking
                const sessionActivityService = await getSessionActivityService();
                sessionActivityService.initialize()

                // Initialize debug session service (restores visibility state from session storage)
                const debugSessionService = await getDebugSessionService();
                debugSessionService.initialize()

                // Get session expiration from /auth/me endpoint
                this.initializeFromAuthMe()

                // Listen for renewal events
                await this.setupRenewalListeners()
            },

            async onUnmounted() {
                console.log('🔐 Session manager unmounted')
                
                // Cleanup listeners
                if (this.unsubscribeRenewal) this.unsubscribeRenewal()
                if (this.unsubscribeSuccess) this.unsubscribeSuccess()
                if (this.unsubscribeFailed) this.unsubscribeFailed()
                if (this.unsubscribeExpired) this.unsubscribeExpired()
                if (this.unsubscribeAuthInit) this.unsubscribeAuthInit()
                
                // Cleanup services - always stop monitoring on unmount
                console.log('🛑 Stopping session monitoring on unmount')
                const sessionRenewalService = await getSessionRenewalService();
                sessionRenewalService.stopMonitoring()
                const sessionActivityService = await getSessionActivityService();
                sessionActivityService.destroy()
                const debugSessionService = await getDebugSessionService();
                debugSessionService.destroy()
            },

            /**
             * Initialize session renewal once the userContext Redux slice is
             * populated by index.tag's initUserContext. Previously this fired
             * its own /auth/me on mount, which beat initImpersonation to the
             * network — the un-Bearered request hit the mock's fallthrough
             * and latched SYSTEM_ADMIN into AuthService's in-flight promise,
             * masking the impersonated role for the rest of the session.
             * Subscribing to Redux lets us piggyback on the authoritative
             * /auth/me that runs after the imp_ token is in state.
             */
            async initializeFromAuthMe() {
                const applyUserContext = async (userData) => {
                    if (!userData || !userData.expiresAt) {
                        if (userData && !userData.expiresAt) {
                            console.warn('⚠️ No expiresAt in userContext - session renewal not initialized')
                        }
                        return
                    }
                    const expiresAt = new Date(userData.expiresAt)
                    const sessionRenewalService = await getSessionRenewalService()
                    sessionRenewalService.initialize(expiresAt)
                    console.log('✅ Session renewal initialized:', {
                        expiresAt: userData.expiresAt,
                        minutesRemaining: Math.floor((expiresAt - new Date()) / 60000),
                        isImpersonating: userData.impersonating || false
                    })
                }

                const initialContext = this.store.getState().userContext
                if (initialContext) {
                    await applyUserContext(initialContext)
                    return
                }

                // Not yet populated — subscribe and unsubscribe on first hit.
                this.unsubscribeAuthInit = this.store.subscribe(() => {
                    const ctx = this.store.getState().userContext
                    if (!ctx) return
                    if (this.unsubscribeAuthInit) {
                        this.unsubscribeAuthInit()
                        this.unsubscribeAuthInit = null
                    }
                    applyUserContext(ctx).catch(err =>
                        console.error('❌ Failed to initialize session renewal:', err))
                })
            },

            /**
             * Setup listeners for renewal service events
             */
            async setupRenewalListeners() {
                // Load service
                const sessionRenewalService = await getSessionRenewalService();

                // Show inactivity prompt when user is inactive in renewal window
                this.unsubscribeRenewal = sessionRenewalService.on('show-inactivity-prompt', (data) => {
                    console.log('⚠️ Showing inactivity prompt')
                    
                    // Get modal reference
                    const modals = this.$$('inactivity-warning-modal')
                    const modal = modals && modals.length > 0 && modals[0]._tag ? modals[0]._tag : null
                    
                    if (modal && typeof modal.show === 'function') {
                        modal.show(data.timeRemaining)
                    } else {
                        console.error('❌ Inactivity modal not found or not initialized')
                    }
                })

                // Handle successful renewal
                this.unsubscribeSuccess = sessionRenewalService.on('renewal-success', (data) => {
                    console.log('✅ Session renewed successfully:', {
                        newExpiresAt: data.expiresAt
                    })
                    
                    // Optionally show a brief success notification
                    // Could add a toast notification here
                })

                // Handle renewal failure
                this.unsubscribeFailed = sessionRenewalService.on('renewal-failed', (error) => {
                    console.error('❌ Session renewal failed:', error)

                    // Show error and redirect to login
                    alert(this.locale.t('alert_session_renew_failed'))
                    window.location.href = '#login?reason=renewal-failed'
                })

                // Handle session expiration - use exact same logic as manual logout
                this.unsubscribeExpired = sessionRenewalService.on('session-expired', async () => {
                    console.log('⏰ Session expired')
                    
                    try {
                        // Call backend logout API
                        const authService = await getAuthService();
                        await authService.logout()
                        console.log('✅ Logout endpoint called successfully')
                    } catch (error) {
                        console.warn('⚠️ Failed to call logout endpoint:', error)
                        // Continue with redirect even if logout fails
                    }
                    
                    try {
                         // Clear all authentication state (matching navbar's handleLogout exactly)
                         localStorage.removeItem('authToken')
                         localStorage.removeItem('workspace_sliceId')
                         localStorage.removeItem('workspace_sliceName')
                         localStorage.removeItem('last_selected_slice_id')
                         sessionStorage.removeItem('workspace_auto_select_attempted')
                         sessionStorage.clear()  // Clear all session storage
                         console.log('✅ Local/session storage cleared');

                         // Clear Redux store and user context
                         console.log('[Session Manager] Dispatching Redux clear actions...');
                         this.store.dispatch({ type: 'clearImpersonation' })
                         this.store.dispatch({ type: 'setSliceId', payload: null })
                         this.store.dispatch({ type: 'CLEAR_CUSTOMER' })
                         console.log('[Session Manager] Redux actions dispatched');

                         // Use tools to clear user context (this triggers navbar update)
                         console.log('[Session Manager] Calling tools.clearUserContext()...');
                         await this.tools.clearUserContext()
                         console.log('[Session Manager] tools.clearUserContext() completed');

                         // Verify state after clearing
                         const finalState = this.store.getState();
                         console.log('[Session Manager] Final Redux state:', {
                             userContext: finalState.userContext ? 'exists' : 'NULL',
                             customer: finalState.customer ? 'exists' : 'NULL',
                             sliceId: finalState.sliceId
                         });

                         console.log('✅ All authentication state cleared')
                     } catch (error) {
                         console.error('❌ Failed to clear authentication state:', error)
                         // Try to still navigate even if clearing fails
                     }

                    // Give Redux subscribers and Riot components time to process state changes
                    // This is critical to ensure navbar detects the cleared userContext before navigation
                    await new Promise(resolve => setTimeout(resolve, 200))

                    // Navigate to login page
                    this.tools.navigateTo('login')
                })
            }
        }
    </script>
</session-manager>